import { useEffect, useState } from "react";
import { useTheme } from "@/context/ThemeProvider";
import StructuredData from "@/components/seo/StructuredData";
import { SOFTWARE_APPLICATION_JSON_LD, buildBreadcrumbsJsonLd } from "@/components/seo/schema";
import { Skeleton } from "@/components/ui/skeleton";
import { Reveal } from "@/components/Reveal";

import NavbarMdx        from "@/components/landing/NavbarMdx";
import HeroSectionMdx   from "@/components/landing/HeroSectionMdx";
import TestimonialsRibbon from "@/components/landing/TestimonialsRibbon";
import MetricsMatrix    from "@/components/landing/MetricsMatrix";
import AiCopilotComparison from "@/components/landing/AiCopilotComparison";
import ExchangeFootprint from "@/components/landing/ExchangeFootprint";
import LiveTerminalShowcase from "@/components/landing/LiveTerminalShowcase";
import PricingSectionMdx from "@/components/landing/PricingSectionMdx";
import ComplianceFooter from "@/components/landing/ComplianceFooter";
import ObondoTrialModal from "@/components/landing/ObondoTrialModal";
import ExitIntentModal  from "@/components/landing/ExitIntentModal";

// Mirrors the real page's navbar/hero/metrics-grid shape below so nothing
// jumps once the real sections swap in.
function LandingSkeleton({ isDark }) {
    return (
        <div className={`min-h-screen ${isDark ? "bg-[#090D16] text-[#F9FAFB]" : "bg-white text-[#090D16]"}`} data-testid="landing-skeleton">
            <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
                <Skeleton className="h-8 w-40" />
                <Skeleton className="h-9 w-28" />
            </div>
            <div className="max-w-4xl mx-auto px-6 py-20 text-center space-y-4">
                <Skeleton className="h-6 w-48 mx-auto" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-3/4 mx-auto" />
                <Skeleton className="h-5 w-2/3 mx-auto" />
                <Skeleton className="h-12 w-56 mx-auto mt-6" />
            </div>
            <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-6">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
            </div>
        </div>
    );
}

/**
 * Melian Dialogue — Landing page v2.
 * Sections: Navbar → Hero → MetricsMatrix → ExchangeFootprint →
 *           LiveTerminalShowcase → Pricing → ComplianceFooter.
 * All copy 60%-reduced. Fonts: Inter (H1/H2/CTA) + Roboto (body).
 * Data scale: blue-to-orange (#2563EB → #6B7280 → #EA580C). No red/green.
 */
export default function Landing() {
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const [trialOpen, setTrialOpen] = useState(false);
    // Stays static until the initial page load (fonts, images, and each
    // section's own data fetch kicked off on mount — leaderboard, trial
    // signup ticker, market intel, etc.) has had a chance to settle, so we
    // never flash a half-populated page.
    const [ready, setReady] = useState(
        () => typeof document !== "undefined" && document.readyState === "complete",
    );

    useEffect(() => {
        if (ready) return;
        const onLoad = () => setReady(true);
        window.addEventListener("load", onLoad);
        return () => window.removeEventListener("load", onLoad);
    }, [ready]);

    const startTrial = () => setTrialOpen(true);

    if (!ready) return <LandingSkeleton isDark={isDark} />;

    return (
        <div className={`min-h-screen ${isDark ? "bg-[#090D16] text-[#F9FAFB]" : "bg-white text-[#090D16]"}`}>
            <StructuredData id="landing-app"    json={SOFTWARE_APPLICATION_JSON_LD} />
            <StructuredData id="landing-crumbs" json={buildBreadcrumbsJsonLd([{ name: "Home", url: "/" }])} />

            <NavbarMdx onStartTrial={startTrial} />

            <HeroSectionMdx     isDark={isDark} onStartTrial={startTrial} />
            <Reveal><TestimonialsRibbon isDark={isDark} /></Reveal>
            <Reveal><MetricsMatrix      isDark={isDark} /></Reveal>
            <Reveal><AiCopilotComparison isDark={isDark} /></Reveal>
            <Reveal><ExchangeFootprint  isDark={isDark} /></Reveal>
            <Reveal><LiveTerminalShowcase isDark={isDark} /></Reveal>
            <Reveal><PricingSectionMdx  isDark={isDark} onStartTrial={startTrial} /></Reveal>
            <ComplianceFooter   isDark={isDark} />

            <ObondoTrialModal open={trialOpen} onClose={() => setTrialOpen(false)} isDark={isDark} />
            <ExitIntentModal   isDark={isDark} />
        </div>
    );
}
